import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useStoreState } from "easy-peasy";

import { Button, Modal, Typography } from "stories/components";
import { Input } from "stories/forms";
import { TableAuto } from "stories/tables";
import FileDownloadButton from "components/Buttons/FileDownloadButton/FileDownloadButton";
import usePermissions from "hooks/usePermissions";
import { getDate } from "services/datetime";
import { httpGetFundValuationReports } from "services/fund_valuation_reports";

import FundValuationReportEdit from "views/app/FundValuationReportEdit/FundValuationReportEdit";
import FundFairValueCalculation from "views/app/FundFairValueCalculation/FundFairValueCalculation";
import { formatAmount, formatPrecise } from "views/app/FundValuationReportEdit/fundValuationPayload";

const PAGE_SIZE = 30;

// TableAuto renders `cell.content` only when it is truthy and otherwise the cell itself,
// so an empty value must be a plain string, never { content: "" } (React error #31).
const right = (content) => (content ? { content, className: "text-right" } : "");

const FundValuationReportsList = ({ fundId, fundName, setAddFairValue }) => {
  const { t } = useTranslation();
  const { canWrite } = usePermissions();
  const portfolios = useStoreState((state) => state.portfolio?.portfolios) || [];
  const activePortfolio = useStoreState((state) => state.portfolio?.portfolio);

  const [filters, setFilters] = useState({ portfolio: "", limit: PAGE_SIZE, offset: 0 });
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, report: null });

  const getReports = useCallback(() => {
    if (!fundId) {
      return;
    }
    const params = { private_investment_fund: fundId, limit: filters.limit, offset: filters.offset };
    if (filters.portfolio) {
      params.portfolio = filters.portfolio;
    }
    setLoading(true);
    httpGetFundValuationReports(params)
      .then((res) => {
        setRows(res?.data?.results || []);
        setCount(res?.data?.count || 0);
      })
      .catch(() => {
        setRows([]);
        setCount(0);
      })
      .finally(() => setLoading(false));
  }, [fundId, filters]);

  useEffect(() => {
    getReports();
  }, [getReports]);

  const openNew = useCallback(() => setModal({ open: true, report: null }), []);
  const openEdit = (report) => setModal({ open: true, report });
  const closeModal = () => setModal({ open: false, report: null });
  const resolveModal = () => {
    closeModal();
    getReports();
  };

  // The page's top-right action area, as with splits and dividends.
  useEffect(() => {
    if (!setAddFairValue) {
      return undefined;
    }
    setAddFairValue(
      canWrite ? (
        <div className="d-flex justify-content-end align-bottom">
          <Button color="secondary" onClick={openNew}>
            {t("fund_valuation_report_new", "Uusi raportti")}
          </Button>
        </div>
      ) : null
    );
    return () => setAddFairValue(null);
  }, [canWrite, openNew, setAddFairValue, t]);

  const portfolioIds = portfolios.map((p) => String(p.id));
  const defaultPortfolio =
    filters.portfolio ||
    (activePortfolio?.id && portfolioIds.includes(String(activePortfolio.id)) ? activePortfolio.id : "");

  const headers = [
    { label: t("fund_valuation_report_portfolio", "Salkku"), key: "portfolio_name" },
    { label: t("fund_valuation_report_value_date", "Arvopäivä"), key: "value_date" },
    { label: t("fund_valuation_report_report_period", "Raportointijakso"), key: "report_period" },
    { label: t("fund_valuation_report_market_value", "Käypä arvo"), key: "market_value" },
    { label: t("fund_valuation_report_owned_shares", "Kappalemäärä"), key: "owned_shares" },
    { label: t("fund_valuation_report_value_per_share", "Osuuden arvo"), key: "value_per_share" },
    { label: t("fund_valuation_report_fund_size", "Rahaston koko"), key: "fund_size" },
    { label: t("fund_valuation_report_fund_commitment", "Sitoumus"), key: "fund_commitment" },
    { label: t("fund_valuation_report_left_commitment", "Jäljellä oleva sitoumus"), key: "left_commitment" },
    { label: t("fund_valuation_report_report_received", "Raportti saapui"), key: "report_received" },
    { label: t("fund_valuation_report_ifrs_date", "IFRS-päivä"), key: "ifrs_date" },
    { label: t("fund_valuation_report_attachment_short", "Liite"), key: "attachment" },
    { label: " ", key: "actions" },
  ];

  const tableRows = rows.map((row) => ({
    ...row,
    portfolio_name: row.portfolio_name,
    value_date: getDate(row.value_date),
    report_period: row.report_period || "",
    market_value: right(formatAmount(row.market_value)),
    owned_shares: right(formatPrecise(row.owned_shares)),
    value_per_share: right(formatPrecise(row.value_per_share)),
    fund_size: right(formatAmount(row.fund_size)),
    fund_commitment: right(formatAmount(row.fund_commitment)),
    left_commitment: right(formatAmount(row.left_commitment)),
    report_received: getDate(row.report_received),
    ifrs_date: getDate(row.ifrs_date),
    attachment: row.attachment_name ? (
      <FileDownloadButton showPreview fileModel="FundValuationReport" fileId={row.id} fileName={row.attachment_name} />
    ) : (
      ""
    ),
    actions: canWrite ? (
      <div className="d-flex justify-content-end">
        <Button className="btn-sm" color="secondary" onClick={() => openEdit(row)}>
          {t("fund_valuation_report_edit", "Muokkaa")}
        </Button>
      </div>
    ) : (
      ""
    ),
  }));

  const handlePortfolioFilter = (e) => {
    const portfolio = e.target.value;
    setFilters((f) => ({ ...f, portfolio, offset: 0 }));
  };

  const paginationPrevious = () =>
    setFilters((f) => ({ ...f, offset: Math.max(0, f.offset - f.limit) }));
  const paginationNext = () =>
    setFilters((f) => (f.offset + f.limit >= count ? f : { ...f, offset: f.offset + f.limit }));

  return (
    <>
      <FundFairValueCalculation fundId={fundId} portfolio={filters.portfolio} />

      <div className="d-flex align-items-end mb-3">
        <div style={{ minWidth: "240px" }}>
          <Input
            type="select"
            name="fund_valuation_portfolio_filter"
            label={t("fund_valuation_report_portfolio", "Salkku")}
            value={filters.portfolio}
            onChange={handlePortfolioFilter}
          >
            <option value="">{t("fund_valuation_report_all_portfolios", "Kaikki salkut")}</option>
            {portfolios.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </Input>
        </div>
      </div>

      {!loading && rows.length === 0 ? (
        <Typography className="text-muted p-3">{t("fund_valuation_report_empty", "Ei raportteja")}</Typography>
      ) : (
        <TableAuto
          name="fund_valuation_reports"
          showRowNumber={false}
          showId={false}
          checkboxes={false}
          headers={headers}
          headerColor="light"
          rows={tableRows}
          loading={loading}
          pagination={true}
          paginationPrevious={paginationPrevious}
          paginationNext={paginationNext}
          paginationOffset={filters.offset}
          paginationLimit={filters.limit}
          paginationCount={count}
          setFilters={setFilters}
          filters={filters}
          sortBackend={false}
        />
      )}

      <Modal
        isOpen={modal.open}
        toggleModal={closeModal}
        showHeader={false}
        size="modal-lg"
        classNameBody="p-0 padding-0"
      >
        {modal.open ? (
          <FundValuationReportEdit
            key={modal.report?.id || "new"}
            fundId={fundId}
            fundName={fundName}
            report={modal.report}
            portfolios={portfolios}
            defaultPortfolio={defaultPortfolio}
            handleResolve={resolveModal}
            handleReject={closeModal}
          />
        ) : null}
      </Modal>
    </>
  );
};

export default FundValuationReportsList;
